import { useRef } from 'react';

import { commitSpan } from './commit-span';
import { previewSpanFor } from './drag-math';
import { isPreviewStale, useSpanPreview } from './use-span-preview';

import type { DragMode } from './drag-math';
import type { SpanPreview } from './use-span-preview';
import type { GanttItem, GanttPatch, Span } from '../types';
import type React from 'react';

export type BarKeyboard = {
  previewSpan?: Span;
  onKeyDown: (event: React.KeyboardEvent) => void;
  /** Wire to the bar's own `onBlur`. Not asked for by spec §8 in so many words, but leaving focus
   * with an uncommitted preview still showing (and nothing left that can ever clear it, since
   * `Escape` requires the element still be focused to receive the keydown) is a stuck-preview bug
   * a keyboard-only user would have no way out of. Cancels exactly like `Escape` would. */
  onBlur: () => void;
};

/**
 * The keyboard half of spec §8's "keyboard equivalence": arrow keys move a bar by one day,
 * `Shift`+arrows resize it, `Enter` commits, `Escape` cancels. Reuses `previewSpanFor` directly
 * and routes every commit through `commitSpan` — the exact same functions `useBarDrag` uses — so a
 * keyboard-driven commit and a pointer-driven commit for the identical day delta produce identical
 * patches, and a rejected commit clears the preview identically on both paths. See `commit-span.ts`
 * for why that sharing is load-bearing rather than incidental.
 *
 * DECISION, since spec §8 says only "Shift+arrows resize" without saying which edge:
 * `Shift+ArrowLeft`/`Shift+ArrowRight` resize the END edge (`dayDelta` -1 / +1);
 * `Shift+Alt+ArrowLeft`/`Shift+Alt+ArrowRight` resize the START edge, with the same per-key
 * `dayDelta`. Both edges get full grow-AND-shrink control this way — the earlier version of this
 * hook shipped only the END-edge binding, leaving the start date completely unreachable from the
 * keyboard, which is an accessibility gap (spec §8: "There is no keyboard path at all today" — the
 * point of this hook) rather than a missing nice-to-have, since the pointer path can already do
 * this via a left-edge grab.
 *
 * Modifiers considered and rejected for the start edge, and why:
 * - Bare `Alt+arrows` alone: on Windows, Alt+Left/Right IS the browser back/forward shortcut in
 *   every major browser, so this would either be swallowed before reaching the page or fight the
 *   browser's own navigation depending on focus.
 * - Bare `Ctrl+arrows`: on macOS, Ctrl+Left/Right is the SYSTEM (not browser) "Move to space on
 *   the left/right" (Mission Control) shortcut, intercepted at the WindowServer level in most
 *   configurations before any web page ever sees the keydown.
 * `Shift+Alt+arrows` is not a documented OS- or browser-level shortcut on either platform, and it
 * extends rather than replaces the already-shipped `Shift+arrows` binding — `Shift` alone still
 * means exactly what it meant before this change.
 *
 * C2 — WHY THE KEYBOARD IS REFUSED MID-DRAG RATHER THAN CANCELLING THE DRAG. The two modes have
 * to be mutually exclusive, and the two directions are NOT symmetric, so they get different
 * treatments:
 *
 * - A keyboard preview OUTLIVES its keystroke: it stands, uncommitted, until `Enter`, `Escape`,
 *   `Tab`, or a blur. So a pointerdown can and does land on top of one, and `useBarDrag` deals
 *   with it by ADOPTING it (the drag continues from the drawn span, and the single shared store
 *   means there is nothing left to resurface afterwards).
 * - A drag preview does NOT outlive its gesture: `useBarDrag` clears it on pointerup, pointercancel
 *   and its own `Escape`. The only window in which a keystroke can meet one is with the button
 *   physically held down. "Cancel the drag from the keyboard" is unimplementable from here without
 *   reaching into the other hook's live listener set: clearing the shared preview would leave those
 *   listeners running with no preview, and the next `pointermove` would simply re-open one — or
 *   worse, the release would commit whatever `latestPreview` the gesture last computed. The mid-drag
 *   cancel that DOES exist is `Escape`, handled by `useBarDrag`'s own `window` keydown listener,
 *   which tears the gesture down properly.
 *
 * So: while a gesture is live, `suspended` makes every binding here a no-op, including `Escape`
 * (the drag's own window listener owns that key for the duration) and the blur cancel (the drag,
 * not focus, owns the preview). One preview, one owner, at every instant.
 *
 * N3 — "AT EVERY INSTANT" NEEDED ONE MORE THING TO BE TRUE. `suspended` covers the window in which
 * a gesture is LIVE. It does not cover the window in which a gesture has ENDED but its commit has
 * not resolved, and both hooks hand `commitSpan` a callback that tears the shared preview down
 * when it does. An unguarded teardown crossing into a later gesture's window is how the invariant
 * broke; `SpanPreview.version` is what makes each settle callback answer "is this still mine?"
 * before it acts. See `clearPreviewIfStillOurs` below and `use-bar-drag.ts`'s `releaseGesture`.
 *
 * N8 — `suspended` NOW SPANS THAT SECOND WINDOW TOO, and this hook did not change to make it so.
 * `suspended` is `drag.mode !== null`, and `useBarDrag` used to drop `dragMode` as soon as nothing
 * was physically HELD — so after two quick drags an older gesture's settle un-suspended this hook
 * while the newer gesture's commit was still outstanding, and an arrow key computed from the base
 * the older commit had just written. `dragMode` is now released on the same `isCurrent` identity
 * check the store uses, which means "a pointer gesture owns the preview" reads as owned from
 * `pointerdown` until THAT gesture's own commit settles. Everything here is unchanged, including
 * `commit()`'s `isPreviewStale` refusal: it guards a preview that SAT between keystrokes, which is
 * a different question from ownership and does not become redundant.
 */
export const useBarKeyboard = (args: {
  span: Span | undefined;
  item: GanttItem;
  disabled?: boolean;
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
  /** The bar's shared preview store; see `useBarDrag`'s identical prop. */
  preview?: SpanPreview;
  /** `true` while a pointer gesture owns the preview. See the doc-comment above. */
  suspended?: boolean;
}): BarKeyboard => {
  const { span, item, disabled, onCommit, suspended } = args;

  const ownPreview = useSpanPreview();
  const preview = args.preview ?? ownPreview;

  // `onSettle` runs after `onCommit` resolves, by which point `preview` is a new object; the ref
  // keeps that callback pointed at the store rather than at the render it was created in.
  const previewRef = useRef(preview);
  previewRef.current = preview;

  /**
   * N3. The settle callback for an `Enter` commit, guarded so it can only tear down the preview it
   * was created for. `onCommit` is a network round-trip, and the store is shared: between `Enter`
   * and its resolution the user can press `Escape` and start a fresh edit, or put the pointer down
   * on the still-pending preview and have `useBarDrag` adopt it. An unguarded `clear()` then wipes
   * a preview this commit never owned — a live drag's, in the pointer case, which is the same
   * silent-wrong-write shape as the defect the drag path's own guard closes. Taking the version
   * BEFORE handing the callback to `commitSpan` is what makes "still mine" answerable later.
   */
  const clearPreviewIfStillOurs = (version: number) => (): void => {
    if (previewRef.current.isCurrent(version)) previewRef.current.clear();
  };

  const step = (mode: DragMode, dayDelta: number): void => {
    if (!span) return;

    // Steps accumulate from the CURRENT preview, not the original span, so repeated arrow presses
    // keep moving from where the last one left off rather than snapping back each time. A preview
    // whose base no longer matches the item's span was computed against dates that have since
    // moved (an optimistic write, a teammate's edit) — that one is dropped and the edit restarts
    // from the item's real span, rather than compounding a stale offset.
    const base = isPreviewStale(preview, span) ? span : preview.span ?? span;

    preview.open(span, previewSpanFor(base, mode, dayDelta));
  };

  const commit = (): void => {
    // Nothing to commit if the bar was never touched this session; `commitSpan` itself already
    // no-ops the network call when `preview` came back unchanged from `span`, but it still needs
    // both values to compare.
    if (!span || !preview.span) return;

    // C2, defect (3). The preview was computed against a span that has since changed — the
    // optimistic write from a pointer commit landed, or a teammate moved the step. Diffing the two
    // would produce a patch describing a move the user never made, and `patchesFor` has no way to
    // tell that from a real edit. Discard instead: the bar falls back to the item's real dates,
    // which is the only state here that is certainly true.
    if (isPreviewStale(preview, span)) {
      preview.clear();

      return;
    }

    commitSpan({
      item,
      span,
      preview: preview.span,
      onCommit,
      onSettle: clearPreviewIfStillOurs(preview.version()),
    });
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (disabled || suspended || !span) return;

    // `Shift` alone resizes the end edge (unchanged); `Shift+Alt` resizes the start edge (see
    // the doc-comment above for why `Alt`/`Ctrl` alone were rejected). Bare `Alt` with no
    // `Shift` carries no meaning here and falls through to a plain `move`, same as before.
    const modeFor = (): DragMode => {
      if (!event.shiftKey) return 'move';

      return event.altKey ? 'resize-start' : 'resize-end';
    };

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        step(modeFor(), -1);

        return;
      case 'ArrowRight':
        event.preventDefault();
        step(modeFor(), 1);

        return;
      case 'Enter':
        if (!preview.span) return;
        event.preventDefault();
        commit();

        return;
      case 'Escape':
        if (!preview.span) return;
        event.preventDefault();
        preview.clear();

        return;
      default:
        return;
    }
  };

  return {
    previewSpan: preview.span,
    onKeyDown,
    // Suspended: the pointer gesture owns the preview, and a blur during it (pointer capture makes
    // this rare, not impossible) must not delete the drag's own state out from under it.
    onBlur: () => {
      if (!suspended) preview.clear();
    },
  };
};
